import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { InMoceanDataService } from '../services/InMoceanDataService';
import { InMoceanValidationService } from '../services/InMoceanValidationService';

/**
 * Real File Upload Card Component
 * Handles actual file upload to Google Cloud Storage and BigQuery
 */
export default function FileUploadCard({ 
  fileType, 
  schema, 
  onUploadComplete, 
  onUploadError 
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (!file) return;
    
    console.log(`📁 File selected for ${fileType}:`, file.name);
    validateAndUpload(file);
  };

  /**
   * Validate file and start real upload
   */
  const validateAndUpload = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setValidationResult(null);
      setShowValidationDetails(false);
      
      // Stage 1: Enhanced validation with progress
      setUploadStage('Starting validation...');
      setUploadProgress(10);
      
      const validation = await InMoceanValidationService.validateFile(
        file, 
        fileType, 
        (stage) => {
          setUploadStage(stage);
          // Progress from 10% to 50% during validation
          if (stage.includes('format')) setUploadProgress(15);
          else if (stage.includes('content')) setUploadProgress(25);
          else if (stage.includes('structure')) setUploadProgress(35);
          else if (stage.includes('quality')) setUploadProgress(45);
          else if (stage.includes('rules')) setUploadProgress(50);
        }
      );
      
      setValidationResult(validation);
      
      // Check if validation passed
      if (!validation.isValid) {
        setUploadStage(`Validation failed: ${validation.errors[0]}`);
        setShowValidationDetails(true);
        return;
      }
      
      // Show validation warnings if any
      if (validation.warnings.length > 0) {
        setUploadStage(`Validation passed with ${validation.warnings.length} warnings`);
        setShowValidationDetails(true);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Show warnings for 2 seconds
      }
      
      // Stage 2: Upload validated data
      setUploadStage('Uploading to cloud storage...');
      setUploadProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUploadStage('Creating database table...');
      setUploadProgress(75);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUploadStage('Loading data to BigQuery...');
      setUploadProgress(90);
      
      // Use the pre-validated data from validation result
      const result = await InMoceanDataService.uploadAndProcessFile(file, fileType, validation.parsedData);
      
      setUploadProgress(100);
      setUploadStage('Upload complete!');
      
      console.log(`✅ Enhanced upload completed for ${fileType}:`, result);
      onUploadComplete(result);
      
      // Reset after success
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStage('');
        setValidationResult(null);
        setShowValidationDetails(false);
      }, 3000);
      
    } catch (error) {
      console.error(`❌ Enhanced upload failed for ${fileType}:`, error);
      setUploadStage(`Error: ${error.message}`);
      onUploadError(error);
      
      // Reset after error
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStage('');
        setValidationResult(null);
        setShowValidationDetails(false);
      }, 5000);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  /**
   * Handle click to select file
   */
  const handleClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="relative w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={(e) => handleFileSelect(e.target.files[0])}
        className="hidden"
      />

      {/* Compact Drag and Drop Area */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative flex items-center justify-between gap-3 px-4 py-3
          border rounded-lg transition-all cursor-pointer
          ${uploading
            ? 'bg-slate-50 border-slate-300 cursor-not-allowed'
            : dragOver
            ? 'bg-blue-50 border-blue-300 shadow-sm'
            : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
          }
        `}
      >
        <div className="flex items-center gap-3 flex-1">
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-400 border-t-transparent"></div>
              <div>
                <p className="text-sm font-medium text-slate-700">Uploading...</p>
                <p className="text-xs text-slate-500">{uploadProgress}% • {uploadStage}</p>
              </div>
            </>
          ) : uploadProgress === 100 ? (
            <>
              <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-700">Complete</p>
                <p className="text-xs text-emerald-600">Upload successful</p>
              </div>
            </>
          ) : uploadStage && uploadStage.includes('Error') ? (
            <>
              <AlertCircle size={20} className="text-rose-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-rose-700">Upload Failed</p>
                <p className="text-xs text-rose-600 break-words" title={uploadStage}>
                  {uploadStage.replace('Error: ', '')}
                </p>
              </div>
            </>
          ) : uploadStage && uploadStage.includes('Validation failed') ? (
            <>
              <AlertCircle size={20} className="text-rose-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-rose-700">Validation Failed</p>
                <p className="text-xs text-rose-600 break-words" title={uploadStage}>
                  {uploadStage.replace('Validation failed: ', '')}
                </p>
              </div>
            </>
          ) : (
            <>
              <Upload size={18} className={dragOver ? 'text-blue-600' : 'text-slate-400'} />
              <div>
                <p className={`text-sm font-medium ${dragOver ? 'text-blue-700' : 'text-slate-700'}`}>
                  {dragOver ? 'Drop file to upload' : 'Drag & drop or click'}
                </p>
                <p className="text-xs text-slate-500">
                  CSV, XLSX • Max {schema.maxSizeGB}GB
                </p>
              </div>
            </>
          )}
        </div>

        {!uploading && uploadProgress !== 100 && (
          <div className="text-xs text-blue-600 font-medium whitespace-nowrap">
            Browse
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg p-3 shadow-lg z-10">
          <div className="space-y-2">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            
            {/* Progress Text */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-600">{uploadStage}</span>
              <span className="font-medium text-gray-900">{uploadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Validation Details */}
      {showValidationDetails && validationResult && (
        <div className={`absolute top-full left-0 right-0 mt-2 border rounded-lg p-4 shadow-lg z-10 ${
          validationResult.isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="space-y-3">
            {/* Validation Summary */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {validationResult.isValid ? (
                  <CheckCircle className="text-green-600" size={16} />
                ) : (
                  <AlertCircle className="text-red-600" size={16} />
                )}
                <span className={`font-medium text-sm ${
                  validationResult.isValid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {validationResult.isValid ? 'Validation Passed' : 'Validation Failed'}
                </span>
              </div>
              <button
                onClick={() => setShowValidationDetails(false)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            </div>

            {/* File Info */}
            {validationResult.info && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-gray-600">
                  Rows: <span className="font-medium">{validationResult.info.totalRows || 0}</span>
                </div>
                <div className="text-gray-600">
                  Size: <span className="font-medium">{validationResult.info.fileSize || 'Unknown'}</span>
                </div>
                {validationResult.info.dataQualityScore && (
                  <div className="text-gray-600">
                    Quality: <span className="font-medium">{validationResult.info.dataQualityScore}%</span>
                  </div>
                )}
                {validationResult.info.missingColumns !== undefined && (
                  <div className="text-gray-600">
                    Missing: <span className="font-medium">{validationResult.info.missingColumns} cols</span>
                  </div>
                )}
              </div>
            )}

            {/* Errors */}
            {validationResult.errors.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-red-800">Errors:</div>
                {validationResult.errors.slice(0, 3).map((error, index) => (
                  <div key={index} className="text-xs text-red-700 flex items-start gap-1">
                    <span className="text-red-500">•</span>
                    <span>{error}</span>
                  </div>
                ))}
                {validationResult.errors.length > 3 && (
                  <div className="text-xs text-red-600">
                    +{validationResult.errors.length - 3} more errors
                  </div>
                )}
              </div>
            )}

            {/* Warnings */}
            {validationResult.warnings.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-yellow-800">Warnings:</div>
                {validationResult.warnings.slice(0, 3).map((warning, index) => (
                  <div key={index} className="text-xs text-yellow-700 flex items-start gap-1">
                    <span className="text-yellow-500">•</span>
                    <span>{warning}</span>
                  </div>
                ))}
                {validationResult.warnings.length > 3 && (
                  <div className="text-xs text-yellow-600">
                    +{validationResult.warnings.length - 3} more warnings
                  </div>
                )}
              </div>
            )}

            {/* Continue Button for Valid Files with Warnings */}
            {validationResult.isValid && validationResult.warnings.length > 0 && (
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={() => setShowValidationDetails(false)}
                  className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                >
                  Continue Upload
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Message */}
      {uploadProgress === 100 && !uploadStage.includes('Error') && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-green-50 border border-green-200 rounded-lg p-3 shadow-lg z-10">
          <div className="flex items-center gap-2 text-green-800 text-sm">
            <CheckCircle size={16} />
            <span>Upload completed successfully!</span>
          </div>
        </div>
      )}

      {/* Error Message Popup */}
      {!uploading && (uploadStage.includes('Error') || uploadStage.includes('Validation failed')) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg z-10">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
              <div className="flex-1">
                <p className="font-medium text-sm text-red-800">
                  {uploadStage.includes('Validation') ? 'Validation Failed' : 'Upload Failed'}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {uploadStage.replace('Error: ', '').replace('Validation failed: ', '')}
                </p>
              </div>
              <button
                onClick={() => setUploadStage('')}
                className="text-red-400 hover:text-red-600 text-sm font-medium"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-blue-50 border-2 border-blue-400 border-dashed rounded-lg flex items-center justify-center z-20">
          <div className="text-center">
            <FileText className="mx-auto text-blue-600 mb-2" size={32} />
            <p className="text-sm font-medium text-blue-700">Drop file to upload</p>
            <p className="text-xs text-blue-600">
              {schema.name} • Max {schema.maxSizeGB}GB
            </p>
          </div>
        </div>
      )}
    </div>
  );
}